import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { App, Button, Input, Space, Tooltip, Typography } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";

interface InlineEditableProps {
  /** Current persisted value to render in read mode. */
  value: string;
  /** Async save callback. Throws on failure; the component reverts on throw. */
  onSave: (next: string) => Promise<void>;
  /** Show the edit affordance. False renders read-only text. */
  canEdit?: boolean;
  /** Render as a multi-line textarea instead of a single-line input. */
  multiline?: boolean;
  /** Placeholder for empty values. Defaults to em dash. */
  emptyPlaceholder?: string;
  /** Optional aria-label for the editor. */
  ariaLabel?: string;
  /** Min rows for the textarea variant. */
  minRows?: number;
  /** Style applied to the read-mode text. */
  readStyle?: CSSProperties;
}

/**
 * Hover-to-reveal inline editor used by the intake-details tabs (OAIP-76).
 *
 * Read mode shows the value with an edit pencil; click swaps to an
 * Input/Textarea + Save/Cancel pair. The save call is delegated to
 * `onSave` so each callsite can plug into `updateIntake(...)` with the
 * right field name. If `onSave` throws, the local draft reverts to the
 * persisted value and a toast surfaces the error.
 *
 * Keyboard:
 *   - Enter        save (single-line only)
 *   - Cmd/Ctrl-Enter save (multiline)
 *   - Escape       cancel
 */
export default function InlineEditable({
  value,
  onSave,
  canEdit = true,
  multiline = false,
  emptyPlaceholder = "—",
  ariaLabel,
  minRows = 3,
  readStyle,
}: InlineEditableProps) {
  const { message } = App.useApp();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<any>(null);

  // Persisted value can change while we're idle (real-time snapshot
  // updates from Firestore). Keep the local draft mirrored when not
  // actively editing so the UI doesn't go stale.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      // Defer so AntD has rendered the input before we focus it.
      setTimeout(() => inputRef.current?.focus?.({ cursor: "end" }), 0);
    }
  }, [editing]);

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const save = async () => {
    const next = draft.trim();
    if (next === (value || "").trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      message.error(err instanceof Error ? err.message : "Failed to save");
      setDraft(value);
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <Typography.Paragraph style={{ marginBottom: 0, ...readStyle }}>
        {value || emptyPlaceholder}
      </Typography.Paragraph>
    );
  }

  if (editing) {
    if (multiline) {
      return (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Input.TextArea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoSize={{ minRows }}
            disabled={saving}
            aria-label={ariaLabel}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void save();
            }}
          />
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              onClick={save}
              loading={saving}
            >
              Save
            </Button>
            <Button size="small" icon={<CloseOutlined />} onClick={cancel} disabled={saving}>
              Cancel
            </Button>
          </Space>
        </Space>
      );
    }
    return (
      <Space style={{ width: "100%" }}>
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={save}
          disabled={saving}
          aria-label={ariaLabel}
          onKeyDown={(e) => {
            if (e.key === "Escape") cancel();
          }}
          style={{ minWidth: 240 }}
        />
        <Button
          type="primary"
          size="small"
          icon={<CheckOutlined />}
          onClick={save}
          loading={saving}
        />
        <Button size="small" icon={<CloseOutlined />} onClick={cancel} disabled={saving} />
      </Space>
    );
  }

  return (
    <Space size="small" align="start" style={{ width: "100%" }}>
      <Typography.Paragraph
        style={{
          marginBottom: 0,
          whiteSpace: "pre-wrap",
          color: value ? undefined : "rgba(0,0,0,0.45)",
          ...readStyle,
        }}
      >
        {value || emptyPlaceholder}
      </Typography.Paragraph>
      <Tooltip title="Edit">
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={() => setEditing(true)}
          aria-label={ariaLabel ? `Edit ${ariaLabel}` : "Edit"}
        />
      </Tooltip>
    </Space>
  );
}
